import {AppUrls} from 'shared/config/model/urls';
import {Pages} from 'shared/config/model/pages';

import React, {useState} from 'react';
import {i18n} from '@lingui/core';
import {t} from '@lingui/core/macro';
import cn from 'classnames';

import BackButton from 'shared/ui/IconButton/BackButton';
import {EditButton, DeleteButton, PauseButton, PlayButton} from 'shared/ui/IconButton/iconButtons';
import IconButton from 'shared/ui/IconButton/IconButton';
import MoreActionsDots from 'shared/ui/MoreActions/MoreActionsDots';
import MoreActionsItem from 'shared/ui/MoreActions/MoreActionsItem';
import Key from 'shared/ui/icons/General/Key';
import IServerOutput from 'features/server/model/Server/IServer';

import ServerModal from '../../ServerModal';
import ServerPauseModal from '../../ServerPauseModal/ServerPauseModal';
import ServerResumeModal from '../../ServerResumeModal/ServerResumeModal';
import ServerRegenerateTokenModal from '../../ServerRegenerateTokenModal/ServerRegenerateTokenModal';
import ServerDeleteModal from '../../ServerDeleteModal/ServerDeleteModal';

import styles from './ServerHeader.module.scss';

type ModalType = 'edit' | 'delete' | 'pause' | 'resume' | 'regenerate' | null;

interface IServerHeaderProps {
    server: IServerOutput,
}

const ServerHeader: React.FC<IServerHeaderProps> = ({server}) => {
    const [activeModal, setActiveModal] = useState<ModalType>(null);

    const handleCloseModal = () => setActiveModal(null);

    return (
        <div className={styles.container}>
            <div className={cn(styles.titleBlock, styles.color)}>
                <BackButton to={AppUrls.SERVERS} text={t`Назад`} className={styles.backButton}/>
                <h3>{i18n._(Pages.SERVER_DETAILS)}</h3>
                <div className={styles.name} title={server.name}>«{server.name}»</div>
                <EditButton
                    title={t`Редактировать`}
                    iconSize={16}
                    onClick={() => setActiveModal('edit')}
                    className={styles.hideMob}
                />
            </div>
            <div className={styles.actionBlock}>
                <div className={styles.actionButtons}>
                    {
                        server.status === 'paused'
                            ? <PlayButton
                                className={cn(styles.actionButton, styles.play)}
                                title={t`Возобновить`}
                                iconSize={16}
                                text={t`Возобновить`}
                                onClick={() => setActiveModal('resume')}
                            />
                            : <PauseButton
                                className={cn(styles.actionButton, styles.pause)}
                                title={t`На паузу`}
                                iconSize={16}
                                text={t`На паузу`}
                                onClick={() => setActiveModal('pause')}
                            />
                    }
                    <IconButton
                        className={cn(styles.actionButton, styles.regenerate)}
                        icon={<Key color="currentColor"/>}
                        title={t`Перевыпустить токен`}
                        onClick={() => setActiveModal('regenerate')}
                    />
                    <DeleteButton
                        className={styles.deleteButton}
                        title={t`Удалить`}
                        iconSize={16}
                        onClick={() => setActiveModal('delete')}
                    />
                </div>
                <div className={styles.menu}>
                    <MoreActionsDots vertical>
                        {
                            server.status === 'paused'
                                ? <MoreActionsItem
                                    label={t`Возобновить`}
                                    onClick={() => setActiveModal('resume')}
                                />
                                : <MoreActionsItem
                                    label={t`На паузу`}
                                    onClick={() => setActiveModal('pause')}
                                />
                        }
                        <MoreActionsItem
                            label={t`Перевыпустить токен`}
                            onClick={() => setActiveModal('regenerate')}
                        />
                        <MoreActionsItem
                            label={t`Редактировать`}
                            onClick={() => setActiveModal('edit')}
                        />
                        <MoreActionsItem
                            label={t`Удалить`}
                            onClick={() => setActiveModal('delete')}
                        />
                    </MoreActionsDots>
                </div>
            </div>

            <ServerModal item={server} open={activeModal === 'edit'} handleClose={handleCloseModal}/>
            <ServerPauseModal item={server} open={activeModal === 'pause'} handleClose={handleCloseModal}/>
            <ServerResumeModal item={server} open={activeModal === 'resume'} handleClose={handleCloseModal}/>
            <ServerRegenerateTokenModal
                item={server}
                open={activeModal === 'regenerate'}
                handleClose={handleCloseModal}
            />
            <ServerDeleteModal item={server} open={activeModal === 'delete'} handleClose={handleCloseModal}/>
        </div>
    );
};

export default ServerHeader;
